import { useMemo, useState } from 'react';
import { Card, Skeleton } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import { ToolCatalogCard } from '../ToolCatalogCard';
import { filterTools } from './filterTools';
import type { ToolsGridFilters, ToolsGridProps } from './ToolsGrid.types';
import { ToolsGridToolbar } from './ToolsGridToolbar';

const SKELETON_COUNT = 8;

const NO_FILTERS: ToolsGridFilters = { search: '', category: null, kind: null };

/**
 * Every tool the workspace can reach, narrowed by the toolbar above it.
 *
 * Filtering is client-side because the whole catalog is already loaded: the
 * category grids each fetch their own tag, and the page asks for all four.
 */
export function ToolsGrid({ tools, isLoading = false, categoryLabels }: ToolsGridProps) {
  const { t } = useTranslation('tools');

  const [filters, setFilters] = useState<ToolsGridFilters>(NO_FILTERS);

  const visibleTools = useMemo(() => filterTools(tools, filters), [tools, filters]);

  return (
    <div data-testid="tools-grid" className="content-block gap-xl">
      <ToolsGridToolbar filters={filters} onChange={setFilters} categoryLabels={categoryLabels} />

      <div
        data-testid="tools-grid-items"
        className="grid grid-cols-2 gap-grid sm:grid-cols-3 lg:grid-cols-4"
      >
        {isLoading
          ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <Card
                key={`tools-grid-skeleton-${index}`}
                variant="outline"
                data-testid="tools-grid-skeleton"
                className="gap-xl rounded-lg border-border-light p-md shadow-none"
              >
                <Skeleton className="size-12 rounded-sm" />
                <div className="flex flex-col gap-2xs">
                  <Skeleton className="h-5 w-4/5" />
                  <Skeleton className="h-4 w-3/5" />
                </div>
              </Card>
            ))
          : visibleTools.map((tool) => <ToolCatalogCard key={tool.id} tool={tool} />)}
      </div>

      {!isLoading && visibleTools.length === 0 && (
        <p className="type-body-md py-3xl text-center text-muted-foreground">
          {/* A workspace with no tools at all is not a workspace whose filters
              excluded everything, and telling it so sends the user hunting for
              a filter they never set. */}
          {tools.length === 0
            ? t('toolsHub.toolCatalog.noToolsAvailable')
            : t('toolsHub.toolCatalog.noToolsMatchYourFilters')}
        </p>
      )}
    </div>
  );
}

ToolsGrid.displayName = 'ToolsGrid';
